import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { CrossIcon } from "../../assets/icons";
import {
  AccountTransactionResponsePaymentTypeEnum,
  AccountTransactionResponseTransactionTypeEnum,
  CreateAccountTransactionRequest,
  CreateAccountTransactionRequestOperationTypeEnum,
  UploadImageImageTypeEnum,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { PaymentType, TransactionType } from "../../utils";
import Transition from "../Transition";
import Api from "../../openapi";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import useTransactionValidation from "./TransactionValidation";

interface ITransactionCrud extends IDialogProps {
  type?: string;
  accountId?: string;
  edit?: any;
}
interface UpdateAccountTransactionRequest extends CreateAccountTransactionRequest {
  id?: string;
}

export default function Transaction(props: ITransactionCrud) {
  // console.log("props", props);
  const defaultValue = {
    transactionType: "",
    operationType: "",
    paymentType: "",
    date: moment().format(),
    description: "",
    amount: "",
  };
  const [formDirty, setFormDirty] = useState(false);
  const [transaction, setTransaction] = useState<any>(defaultValue);
  const dispatch = useDispatch();
  const { validation, setValidation } = useTransactionValidation(transaction);
  const [loading, setLoading] = useState(false);

  const handleFieldChange = (field: any, value?: any) => {
    setFormDirty(true);
    setTransaction((state: any) => ({ ...state, [field]: value }));
  };

  // console.log("transaction", transaction);

  useEffect(() => {
    if (props.edit) {
      setTransaction({
        id: props.edit.id,
        transactionType: props.edit.transactionType,
        operationType: props.edit.operationType,
        paymentType: props.edit.paymentType,
        date: moment(props.edit.date).format(),
        description: props.edit.description,
        amount: props.edit.amount,
      });
    } else {
      setTransaction(defaultValue);
    }
  }, [props.edit]);

  // console.log("plan", plan);
  // console.log("portions", portions);
  // console.log("validation", validation);

  const saveChanges = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    let TransactionReqDto: UpdateAccountTransactionRequest = {
      ...transaction,
    };

    if (props.type === "deposit") {
      TransactionReqDto.operationType = CreateAccountTransactionRequestOperationTypeEnum.Deposit;
    } else if (props.type === "withdraw") {
      TransactionReqDto.operationType = CreateAccountTransactionRequestOperationTypeEnum.Withdraw;
      TransactionReqDto.amount = -1 * Number(TransactionReqDto.amount);
    }

    try {
      setLoading(true);
      let message = "";
      if (props.edit) {
        message = "Transaction updated successfully";
        const { id, ...UpdateTransactionReqDto } = TransactionReqDto;
        await Api.Account.updateTransaction(String(props.accountId), String(id), UpdateTransactionReqDto);
      } else {
        message = "Transaction made successfully";
        await Api.Account.createTransaction(String(props.accountId), TransactionReqDto);
        resetForm();
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };

  const resetForm = () => {
    setTransaction({
      transactionType: "",
      operationType: "",
      paymentType: "",
      date: moment().format(),
      description: "",
      amount: "",
    });
    setValidation((state) => ({ ...state, show: false }));
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "564px",
            maxWidth: "564px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 16px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
            textTransform: "capitalize",
          }}
        >
          {props.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Transaction Type
              </Typography>
              <Select
                value={transaction.transactionType || " "}
                onChange={(e) => handleFieldChange("transactionType", e.target.value)}
              >
                <MenuItem disabled value=" ">
                  <em>Select</em>
                </MenuItem>

                {Object.values(AccountTransactionResponseTransactionTypeEnum)?.map((transactionType) => (
                  <MenuItem key={transactionType} value={transactionType}>
                    {TransactionType[transactionType]}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText
                sx={{ color: "#FF4040" }}
                hidden={!(validation.show && validation.transactionType?.error)}
              >
                {validation.transactionType?.helperText}
              </FormHelperText>
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Payment Type
              </Typography>
              <Select
                value={transaction.paymentType || " "}
                onChange={(e) => handleFieldChange("paymentType", e.target.value)}
              >
                <MenuItem disabled value=" ">
                  <em>Select</em>
                </MenuItem>

                {Object.values(AccountTransactionResponsePaymentTypeEnum)?.map((paymentType) => (
                  <MenuItem key={paymentType} value={paymentType}>
                    {PaymentType[paymentType]}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: "#FF4040" }} hidden={!(validation.show && validation.paymentType?.error)}>
                {validation.paymentType?.helperText}
              </FormHelperText>
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Amount
              </Typography>
              <TextField
                className="form-field"
                value={transaction?.amount}
                error={validation.show && validation.amount?.error}
                helperText={validation.show && validation.amount?.error ? validation.amount.helperText : undefined}
                onChange={(e) => handleFieldChange("amount", e.target.value)}
              />
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Description
              </Typography>
              <TextField
                className="form-field"
                value={transaction?.description}
                // error={validation.show && validation.amount?.error}
                // helperText={
                //   validation.show && validation.amount?.error ? validation.amount.helperText : undefined
                // }
                onChange={(e) => handleFieldChange("description", e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
